import { z } from "zod";
import { ufSchema } from "./calculation.js";
import { environmentSchema } from "./context.js";
/**
 * Browser agent (ADR 0012): one always-on VM drives a visible Chrome against
 * an authorized source (first: IOB Simulador Tributário with BackChannel's
 * dedicated, approved account) inside a daily operating window. It records
 * runs, narrated steps and extracted findings through the runtime API; admins
 * watch the live screen read-only. Findings are review material: they never
 * create, approve, publish or calculate a rule.
 */
/**
 * `IOB_SIMULADOR` is the VM; `WEB_RESEARCH` is the web research lane (per-pair
 * `VERIFY_PAIR` checks), which only writes runs and findings. The IOB queue,
 * its caches and its claims read `IOB_SIMULADOR` rows only.
 */
export const browserAgentSourceSchema = z.enum(["IOB_SIMULADOR", "WEB_RESEARCH"]);
export const browserAgentRunStatusSchema = z.enum(["RUNNING", "SUCCEEDED", "FAILED", "STOPPED"]);
export const browserAgentTriggerSchema = z.enum(["SCHEDULE", "MANUAL"]);
export const browserAgentStepKindSchema = z.enum([
    "SESSION",
    "NAVIGATE",
    "INPUT",
    "CLICK",
    "WAIT",
    "READ",
    "EXTRACT",
    "RECOVER",
    "PAUSE",
    "ERROR",
]);
export const browserAgentFindingOutcomeSchema = z.enum([
    "ST_FOUND",
    "NO_ST_FOUND",
    "INTERNAL_ST_ONLY",
    "NCM_NOT_FOUND",
    "UNREADABLE",
    /** Web research only: the official text did not settle the question. */
    "INCONCLUSIVE",
]);
const ncmSchema = z.string().regex(/^\d{8}$/);
const cestSchema = z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/);
const rateSchema = z.string().regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/);
/**
 * A GCS object written by the agent (step screenshot). Never a URL. The agent
 * writes under `browser-agent/v1/`; a demo reads its preserved copy under
 * `browser-agent/demos/` (the runtime API only accepts `v1/` keys).
 */
export const browserAgentFrameRefSchema = z
    .object({
    bucket: z.string().min(3).max(222),
    key: z
        .string()
        .min(1)
        .max(1024)
        .regex(/^browser-agent\/(v1|demos)\//),
    generation: z.string().regex(/^\d+$/),
    sizeBytes: z
        .number()
        .int()
        .positive()
        .max(5 * 1024 * 1024),
    capturedAt: z.string().datetime(),
})
    .strict();
export const browserAgentLookupSchema = z
    .object({
    ncm: ncmSchema,
    cest: cestSchema.nullable(),
    originUf: ufSchema,
    destinationUf: ufSchema,
    /** Coverage gap that motivated the lookup, when any. */
    gapId: z.string().uuid().nullable(),
    /** Queue task item this lookup belongs to, when any (echoed on the finding). */
    taskItemId: z.string().uuid().nullable().optional(),
})
    .strict();
/** One official document a web finding relied on (web research only). */
export const browserAgentFindingCitationSchema = z
    .object({
    url: z.string().url().max(2_000),
    host: z.string().min(1).max(253),
    /** Reviewed source key the host was allowlisted by. */
    sourceKey: z.string().min(1).max(120).nullable(),
    /** Ingested evidence document, once fetched. */
    evidenceId: z.string().uuid().nullable(),
    /** Verbatim row quote from the fetched text. */
    quote: z.string().max(4_000),
})
    .strict();
/** Deterministic post-extraction checks (web research only). */
export const browserAgentFindingChecksSchema = z
    .object({
    passed: z.boolean(),
    failures: z.array(z.string().min(1).max(120)).max(20),
})
    .strict();
/**
 * One row of IOB's "Produtos Sujeitos a ST - Regras Retroativas" section: a
 * rule that was in force in a closed past window. Both dates are required;
 * a row without a stated end is not a retroactive rule and is dropped.
 */
export const browserAgentRetroactiveRuleSchema = z
    .object({
    cest: cestSchema.nullable(),
    segment: z.string().max(300).nullable(),
    originalMva: rateSchema.nullable(),
    validFrom: z.string().date(),
    validTo: z.string().date(),
    legalBasis: z.string().max(1_000).nullable(),
})
    .strict()
    .refine((rule) => rule.validTo >= rule.validFrom, {
    message: "validTo must not precede validFrom",
    path: ["validTo"],
});
/**
 * What the agent read on the result page, as displayed. Rates are fractions.
 * `retroactiveRules` is IOB only (optional: agents built before it omit it).
 * The trailing optional fields after it are written by the web research lane only.
 */
export const browserAgentFindingDataSchema = z
    .object({
    ncmDescription: z.string().max(1_000).nullable(),
    cest: cestSchema.nullable(),
    segment: z.string().max(300).nullable(),
    originalMva: rateSchema.nullable(),
    validFrom: z.string().date().nullable(),
    validTo: z.string().date().nullable(),
    legalBasis: z.string().max(1_000).nullable(),
    notice: z.string().max(1_000).nullable(),
    retroactiveRules: z.array(browserAgentRetroactiveRuleSchema).max(20).optional(),
    destinationInternalRate: rateSchema.nullable().optional(),
    interstateRate: rateSchema.nullable().optional(),
    citations: z.array(browserAgentFindingCitationSchema).max(20).optional(),
    checks: browserAgentFindingChecksSchema.optional(),
})
    .strict();
/** The queue task (and backend lookup job) a finding answered, when any. */
export const browserAgentFindingTaskSchema = z
    .object({
    id: z.string().uuid(),
    name: z.string().min(1).max(120),
    lookupJobId: z.string().uuid().nullable(),
})
    .strict();
export const browserAgentFindingSchema = z
    .object({
    id: z.string().uuid(),
    runId: z.string().uuid(),
    source: browserAgentSourceSchema,
    lookup: browserAgentLookupSchema,
    outcome: browserAgentFindingOutcomeSchema,
    data: browserAgentFindingDataSchema,
    frame: browserAgentFrameRefSchema.nullable(),
    capturedAt: z.string().datetime(),
    /** Admin read model: the task the finding answered (null = coverage gap lane). */
    task: browserAgentFindingTaskSchema.nullable().optional(),
})
    .strict();
export const browserAgentStepSchema = z
    .object({
    runId: z.string().uuid(),
    sequence: z.number().int().nonnegative(),
    kind: browserAgentStepKindSchema,
    /** Plain-language narration shown to the spectator. No credentials. */
    message: z.string().trim().min(1).max(500),
    url: z.string().url().max(2_000).nullable(),
    lookup: browserAgentLookupSchema.nullable(),
    frame: browserAgentFrameRefSchema.nullable(),
    durationMs: z.number().int().nonnegative().max(3_600_000).nullable(),
    at: z.string().datetime(),
})
    .strict();
/**
 * The IOB agent's last work poll, recorded by the runtime API. The agent polls
 * while idle (no work, outside its schedule, paused), so a recent poll with no
 * open run means a healthy idle agent, not a silent one.
 */
export const browserAgentPresenceSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    lastPollAt: z.string().datetime().nullable(),
    /** Lookups handed out by that poll. */
    lastPollLookups: z.number().int().nonnegative().nullable(),
})
    .strict();
export const browserAgentRunSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    environment: environmentSchema,
    source: browserAgentSourceSchema,
    trigger: browserAgentTriggerSchema,
    status: browserAgentRunStatusSchema,
    agentVersion: z.string().min(1).max(80),
    startedAt: z.string().datetime(),
    heartbeatAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    currentMessage: z.string().max(500).nullable(),
    currentLookup: browserAgentLookupSchema.nullable(),
    lookupsDone: z.number().int().nonnegative(),
    findingsCount: z.number().int().nonnegative(),
    errorsCount: z.number().int().nonnegative(),
    lastError: z.string().max(1_000).nullable(),
    stopRequested: z.boolean(),
    /**
     * True while the agent records the screen for this run (admin read model
     * only; optional so agents built before recordings still parse runs).
     */
    recording: z.boolean().optional(),
    /** Finalized screen recordings reported for this run. */
    recordingsCount: z.number().int().nonnegative().optional(),
    revision: z.number().int().positive(),
})
    .strict();
const pageOf = (item) => z
    .object({
    items: z.array(item).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
export const browserAgentRunPageSchema = pageOf(browserAgentRunSchema);
export const browserAgentFindingPageSchema = pageOf(browserAgentFindingSchema);
export const browserAgentStepListSchema = z
    .object({ items: z.array(browserAgentStepSchema).max(500), lastSequence: z.number().int() })
    .strict();
/* -- Runtime (agent → API), Google ID token with BROWSER_AGENT capability --- */
export const browserAgentRunStartSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    runId: z.string().uuid(),
    environment: environmentSchema,
    source: browserAgentSourceSchema,
    trigger: browserAgentTriggerSchema,
    agentVersion: z.string().min(1).max(80),
    startedAt: z.string().datetime(),
})
    .strict();
/** Heartbeat/progress; the response tells the agent whether to stop. */
export const browserAgentRunUpdateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    runId: z.string().uuid(),
    status: browserAgentRunStatusSchema,
    currentMessage: z.string().max(500).nullable(),
    currentLookup: browserAgentLookupSchema.nullable(),
    lookupsDone: z.number().int().nonnegative(),
    errorsCount: z.number().int().nonnegative(),
    lastError: z.string().max(1_000).nullable(),
    /** Whether the screen is being recorded now. Omitted by agents that never record. */
    recording: z.boolean().optional(),
    at: z.string().datetime(),
})
    .strict();
export const browserAgentRunControlSchema = z
    .object({ stopRequested: z.boolean(), status: browserAgentRunStatusSchema })
    .strict();
export const browserAgentStepBatchSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    runId: z.string().uuid(),
    steps: z.array(browserAgentStepSchema).min(1).max(50),
})
    .strict();
export const browserAgentFindingCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    runId: z.string().uuid(),
    source: browserAgentSourceSchema,
    lookup: browserAgentLookupSchema,
    outcome: browserAgentFindingOutcomeSchema,
    data: browserAgentFindingDataSchema,
    frame: browserAgentFrameRefSchema.nullable(),
    capturedAt: z.string().datetime(),
})
    .strict();
/* -- Settings (admin-editable, Brasília time) -------------------------------- */
/** The agent always works on Brasília time; the schedule is edited in it. */
export const BROWSER_AGENT_TIMEZONE = "America/Sao_Paulo";
/** Brasília has had no daylight saving time since 2019. */
export const BROWSER_AGENT_UTC_OFFSET_MINUTES = -180;
export const BROWSER_AGENT_MIN_PAUSE_SECONDS = 20;
export const BROWSER_AGENT_MAX_LOOKUPS_PER_HOUR = 60;
const clockSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const clockMinutes = (clock) => Number(clock.slice(0, 2)) * 60 + Number(clock.slice(3, 5));
/**
 * One block of the weekly schedule. `end` before `start` means the block runs
 * past midnight into the next day (e.g. 17:00–07:00, or 14:00–00:00 for
 * "until midnight"). `end` equal to `start` is a full 24 h from `start` to
 * the same clock the next day (07:00–07:00 runs Saturday 07:00 → Sunday 07:00
 * and Sunday 07:00 → Monday 07:00). `allDay` covers the calendar day
 * 00:00–24:00 and ignores start/end.
 */
export const browserAgentScheduleBlockSchema = z
    .object({
    enabled: z.boolean(),
    allDay: z.boolean(),
    start: clockSchema,
    end: clockSchema,
})
    .strict();
/**
 * Three blocks, as operations plans them (IOB usage agreed by BackChannel,
 * recorded 2026-09-26): Monday–Thursday, Friday, and the weekend. Each block
 * applies to each of its days.
 *
 * Operating rule recorded 2026-09-28: off Monday–Friday 07:00–17:00 BRT
 * (Brasília business hours), on at every other time.
 */
export const browserAgentWeeklyScheduleSchema = z
    .object({
    mondayToThursday: browserAgentScheduleBlockSchema,
    friday: browserAgentScheduleBlockSchema,
    /** Saturday and Sunday. */
    weekend: browserAgentScheduleBlockSchema,
})
    .strict();
/**
 * The 2026-09-28 rule: weeknights 17:00 → 07:00 (Thursday's ends Friday 07:00,
 * Friday's ends Saturday 07:00) and weekend days 07:00 → 07:00, so the agent
 * runs without a gap from Friday 17:00 to Monday 07:00. 118 h a week (168 h less 5 × 10 h).
 */
export const BROWSER_AGENT_DEFAULT_SCHEDULE = {
    mondayToThursday: { enabled: true, allDay: false, start: "17:00", end: "07:00" },
    friday: { enabled: true, allDay: false, start: "17:00", end: "07:00" },
    weekend: { enabled: true, allDay: false, start: "07:00", end: "07:00" },
};
const MINUTES_PER_DAY = 1440;
export const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;
/**
 * Active intervals of the week in Brasília minutes since Monday 00:00,
 * sorted and merged; each is [start, end) with 0 ≤ start < end ≤ 10080.
 * A timed block whose end is at or before its start ends the next day, so an
 * equal start and end is exactly 24 h; Sunday's block wraps into Monday.
 */
export function browserAgentScheduleIntervals(schedule) {
    const raw = [];
    for (let day = 0; day < 7; day += 1) {
        const block = day <= 3 ? schedule.mondayToThursday : day === 4 ? schedule.friday : schedule.weekend;
        if (!block.enabled)
            continue;
        const dayStart = day * MINUTES_PER_DAY;
        if (block.allDay) {
            raw.push([dayStart, dayStart + MINUTES_PER_DAY]);
            continue;
        }
        const start = dayStart + clockMinutes(block.start);
        const endClock = clockMinutes(block.end);
        const end = endClock > clockMinutes(block.start)
            ? dayStart + endClock
            : dayStart + MINUTES_PER_DAY + endClock;
        if (end <= MINUTES_PER_WEEK)
            raw.push([start, end]);
        else {
            raw.push([start, MINUTES_PER_WEEK]);
            raw.push([0, end - MINUTES_PER_WEEK]);
        }
    }
    raw.sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const [start, end] of raw) {
        const last = merged.at(-1);
        if (last && start <= last[1])
            last[1] = Math.max(last[1], end);
        else if (end > start)
            merged.push([start, end]);
    }
    return merged;
}
/** Minutes since Monday 00:00 Brasília for an instant. */
export function brasiliaMinuteOfWeek(at) {
    const local = new Date(at.getTime() + BROWSER_AGENT_UTC_OFFSET_MINUTES * 60_000);
    const mondayBased = (local.getUTCDay() + 6) % 7;
    return mondayBased * MINUTES_PER_DAY + local.getUTCHours() * 60 + local.getUTCMinutes();
}
export function browserAgentScheduleActiveAt(schedule, at) {
    const minute = brasiliaMinuteOfWeek(at);
    return browserAgentScheduleIntervals(schedule).some(([start, end]) => minute >= start && minute < end);
}
export function browserAgentWeeklyActiveMinutes(schedule) {
    return browserAgentScheduleIntervals(schedule).reduce((sum, [start, end]) => sum + end - start, 0);
}
/**
 * When `workMinutes` of active schedule time will have elapsed from `from`
 * (the ETA helper). Null when the schedule has no active time.
 */
export function browserAgentScheduleFinishAt(schedule, from, workMinutes) {
    const intervals = browserAgentScheduleIntervals(schedule);
    const first = intervals[0];
    if (!first)
        return null;
    let remaining = Math.max(0, workMinutes);
    const startMinute = brasiliaMinuteOfWeek(from);
    // Seconds within the current minute are ignored; ETAs are approximate.
    let cursor = startMinute;
    let elapsed = 0;
    for (let guard = 0; guard < 10_000; guard += 1) {
        const inWeek = ((cursor % MINUTES_PER_WEEK) + MINUTES_PER_WEEK) % MINUTES_PER_WEEK;
        const weekBase = cursor - inWeek;
        const current = intervals.find(([start, end]) => inWeek >= start && inWeek < end);
        if (current) {
            const available = current[1] - inWeek;
            if (available >= remaining) {
                elapsed += remaining;
                return new Date(from.getTime() + elapsed * 60_000);
            }
            remaining -= available;
            elapsed += available;
            cursor += available;
            continue;
        }
        const next = intervals.find(([start]) => start > inWeek);
        const target = next ? weekBase + next[0] : weekBase + MINUTES_PER_WEEK + first[0];
        elapsed += target - cursor;
        cursor = target;
    }
    return null;
}
const settingsFields = {
    schedule: browserAgentWeeklyScheduleSchema,
    lookupPauseSeconds: z.number().int().min(BROWSER_AGENT_MIN_PAUSE_SECONDS).max(600),
    maxLookupsPerHour: z.number().int().min(1).max(BROWSER_AGENT_MAX_LOOKUPS_PER_HOUR),
    /** Kill switch from the UI: the VM stays up and takes no IOB work. */
    paused: z.boolean(),
    /** When the task queue is empty, work on open coverage gaps. */
    fallbackToCoverageGaps: z.boolean(),
    /**
     * Record the agent's screen (display :99) while a run is open, starting
     * after the IOB session is established. Recordings back the Demos section.
     */
    recordRuns: z.boolean(),
};
export const browserAgentSettingsUpdateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    ...settingsFields,
    /** Omitted by clients that predate recordings: the saved value is kept. */
    recordRuns: settingsFields.recordRuns.optional(),
})
    .strict();
export const browserAgentSettingsSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    timezone: z.literal(BROWSER_AGENT_TIMEZONE),
    ...settingsFields,
    updatedAt: z.string().datetime().nullable(),
    updatedBy: z.string().uuid().nullable(),
    revision: z.number().int().nonnegative(),
})
    .strict();
/* -- IOB credentials (write-only from the UI, stored in Secret Manager) ------ */
export const browserAgentCredentialsUpdateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    username: z.string().trim().min(1).max(320),
    password: z.string().min(1).max(512),
})
    .strict();
/** Never contains the password or the full username. */
export const browserAgentCredentialsStatusSchema = z
    .object({
    configured: z.boolean(),
    /** e.g. "da•••@backchannel.com.br" */
    usernameHint: z.string().max(120).nullable(),
    updatedAt: z.string().datetime().nullable(),
    updatedBy: z.string().uuid().nullable(),
})
    .strict();
/* -- Task queue ---------------------------------------------------------------- */
export const browserAgentTaskScopeSchema = z.discriminatedUnion("kind", [
    /** Every active NCM of the official catalog, optionally limited to chapters. */
    z
        .object({
        kind: z.literal("CATALOG"),
        chapters: z
            .array(z.string().regex(/^\d{2}$/))
            .max(97)
            .default([]),
    })
        .strict(),
    /** NCMs BackChannel actually used: seen in calculations in the last 365 days. */
    z
        .object({ kind: z.literal("SEEN") })
        .strict(),
    /** An explicit list pasted or uploaded by an operator. */
    z
        .object({ kind: z.literal("LIST"), ncms: z.array(ncmSchema).min(1).max(10_000) })
        .strict(),
    /** Created by a backend lookup job; never created by hand. */
    z
        .object({ kind: z.literal("LOOKUP_JOB"), jobId: z.string().uuid() })
        .strict(),
    /** IOB certification follow-up queued by a web research sweep; never created by hand. */
    z
        .object({ kind: z.literal("SWEEP"), sweepId: z.string().uuid() })
        .strict(),
]);
export const browserAgentTaskStatusSchema = z.enum([
    "QUEUED",
    "RUNNING",
    "PAUSED",
    "COMPLETED",
    "CANCELLED",
]);
export const browserAgentTaskItemStatusSchema = z.enum([
    "PENDING",
    "IN_PROGRESS",
    /** Looked up; a finding was recorded. */
    "DONE",
    /** Skipped: a published rule or a recent finding already covers it. */
    "CACHED",
    /** Gave up after the retry budget. */
    "FAILED",
    /** Task cancelled before the item ran. */
    "SKIPPED",
]);
export const browserAgentTaskTotalsSchema = z
    .object({
    items: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    cached: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
})
    .strict();
export const browserAgentTaskSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    name: z.string().min(1).max(120),
    source: browserAgentSourceSchema,
    scope: browserAgentTaskScopeSchema,
    originUf: ufSchema,
    destinationUf: ufSchema,
    status: browserAgentTaskStatusSchema,
    /** Queue order among QUEUED/RUNNING/PAUSED tasks; lower runs first. */
    position: z.number().int().nonnegative(),
    totals: browserAgentTaskTotalsSchema,
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    revision: z.number().int().positive(),
})
    .strict();
export const browserAgentTaskPageSchema = pageOf(browserAgentTaskSchema);
export const browserAgentTaskCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    name: z.string().trim().min(1).max(120),
    scope: browserAgentTaskScopeSchema.refine((scope) => scope.kind !== "LOOKUP_JOB" && scope.kind !== "SWEEP", { message: "Lookup job and sweep tasks are created by their own APIs" }),
    originUf: ufSchema,
    destinationUf: ufSchema,
    /** Skip NCMs a published rule or a finding from the last 30 days covers. */
    skipCovered: z.boolean().default(true),
    placement: z.enum(["END", "FRONT"]).default("END"),
})
    .strict();
export const browserAgentTaskActionSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    action: z.enum(["PAUSE", "RESUME", "CANCEL", "MOVE_UP", "MOVE_DOWN", "MOVE_TO_FRONT"]),
})
    .strict();
export const browserAgentTaskItemSchema = z
    .object({
    id: z.string().uuid(),
    taskId: z.string().uuid(),
    ncm: ncmSchema,
    cest: cestSchema.nullable(),
    status: browserAgentTaskItemStatusSchema,
    attempts: z.number().int().nonnegative(),
    findingId: z.string().uuid().nullable(),
    outcome: browserAgentFindingOutcomeSchema.nullable(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const browserAgentTaskItemPageSchema = pageOf(browserAgentTaskItemSchema);
/**
 * Next lookups for the agent: head of the task queue first (then open coverage
 * gaps when enabled), plus the effective settings the agent must obey.
 */
export const browserAgentWorkSchema = z
    .object({
    lookups: z.array(browserAgentLookupSchema).max(50),
    settings: browserAgentSettingsSchema,
    taskId: z.string().uuid().nullable(),
})
    .strict();
/* -- Screen recordings and demos --------------------------------------------- */
/**
 * The agent records display :99 (1440×900, ~8 fps, H.264/MP4) while a run is
 * open, starting only after the IOB session is established (no login form is
 * ever recorded). Each segment lasts at most 30 minutes; longer runs rotate to
 * `recording-2.mp4`, `recording-3.mp4`, … under the run's frames prefix.
 */
export const BROWSER_AGENT_RECORDING_MIME = "video/mp4";
export const BROWSER_AGENT_RECORDING_MAX_SEGMENT_MS = 30 * 60_000;
/** Generous bound for a 30-minute 1440×900 segment (target is 1–3 MB/min). */
export const BROWSER_AGENT_RECORDING_MAX_BYTES = 1024 * 1024 * 1024;
export const BROWSER_AGENT_RECORDING_KEY_PATTERN = /^browser-agent\/v1\/[0-9a-f-]{36}\/recording-[1-9]\d{0,2}\.mp4$/;
/** Runtime (agent → API): one finalized, uploaded recording segment. */
export const browserAgentRecordingCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    runId: z.string().uuid(),
    /** 1-based segment number; the key ends in `recording-{sequence}.mp4`. */
    sequence: z.number().int().min(1).max(999),
    bucket: z.string().min(3).max(222),
    key: z.string().max(1024).regex(BROWSER_AGENT_RECORDING_KEY_PATTERN),
    generation: z.string().regex(/^[1-9]\d*$/),
    sizeBytes: z.number().int().positive().max(BROWSER_AGENT_RECORDING_MAX_BYTES),
    /** Wall-clock length of the segment, pauses included. */
    durationMs: z
        .number()
        .int()
        .nonnegative()
        .max(BROWSER_AGENT_RECORDING_MAX_SEGMENT_MS + 60_000),
    mime: z.literal(BROWSER_AGENT_RECORDING_MIME),
    /** When the first frame was captured; step offsets are measured from here. */
    startedAt: z.string().datetime(),
})
    .strict();
export const browserAgentRecordingSchema = z
    .object({
    id: z.string().uuid(),
    runId: z.string().uuid(),
    sequence: z.number().int().min(1).max(999),
    bucket: z.string().min(3).max(222),
    /** `browser-agent/v1/…` for a run; `browser-agent/demos/…` inside a demo. */
    key: z
        .string()
        .max(1024)
        .regex(/^browser-agent\/(v1|demos)\//),
    generation: z.string().regex(/^[1-9]\d*$/),
    sizeBytes: z.number().int().positive(),
    durationMs: z.number().int().nonnegative(),
    mime: z.literal(BROWSER_AGENT_RECORDING_MIME),
    startedAt: z.string().datetime(),
    createdAt: z.string().datetime(),
})
    .strict();
export const browserAgentRunRecordingsSchema = z
    .object({
    runId: z.string().uuid(),
    items: z.array(browserAgentRecordingSchema).max(200),
    /** Queue task names this run worked on (from its findings), first first. */
    taskNames: z.array(z.string().min(1).max(120)).max(10),
})
    .strict();
/** Short-lived (10 minute) V4 signed GCS URL; supports HTTP Range requests. */
export const browserAgentRecordingUrlSchema = z
    .object({
    url: z.string().url(),
    expiresAt: z.string().datetime(),
    mime: z.literal(BROWSER_AGENT_RECORDING_MIME),
})
    .strict();
export const browserAgentDemoCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    runId: z.string().uuid(),
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().max(2_000).nullable().optional(),
})
    .strict();
/**
 * A run saved as a demo. Its recordings and frames are copied to
 * `browser-agent/demos/{demoId}/…`, outside the 30-day lifecycle of run
 * frames. Deleting a demo removes only this entry.
 */
export const browserAgentDemoSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    runId: z.string().uuid(),
    title: z.string().min(1).max(120),
    description: z.string().max(2_000).nullable(),
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    revision: z.number().int().positive(),
    run: z
        .object({
        status: browserAgentRunStatusSchema,
        startedAt: z.string().datetime(),
        finishedAt: z.string().datetime().nullable(),
        lookupsDone: z.number().int().nonnegative(),
        findingsCount: z.number().int().nonnegative(),
        errorsCount: z.number().int().nonnegative(),
    })
        .strict(),
    /** Sum of the recording segments. */
    durationMs: z.number().int().nonnegative(),
    recordingsCount: z.number().int().nonnegative(),
    /** Distinct origin → destination routes and NCMs among the findings. */
    routesCount: z.number().int().nonnegative(),
    ncmCount: z.number().int().nonnegative(),
    /** First step frame (the demo's copy when preserved). */
    poster: browserAgentFrameRefSchema.nullable(),
})
    .strict();
export const browserAgentDemoPageSchema = pageOf(browserAgentDemoSchema);
/** Everything the demo player needs; frame and recording keys point at the demo copies. */
export const browserAgentDemoDetailSchema = z
    .object({
    demo: browserAgentDemoSchema,
    run: browserAgentRunSchema,
    steps: z.array(browserAgentStepSchema).max(5_000),
    findings: z.array(browserAgentFindingSchema).max(5_000),
    recordings: z.array(browserAgentRecordingSchema).max(200),
})
    .strict();
/**
 * Where a moment of the run sits in its recordings: the segment that covers
 * `at` and the offset in seconds from that segment's start. Null when no
 * segment covers it (before recording started, or during a gap).
 */
export function browserAgentRecordingOffset(recordings, at) {
    const instant = Date.parse(at);
    if (Number.isNaN(instant))
        return null;
    const ordered = [...recordings].sort((a, b) => a.sequence - b.sequence);
    for (const recording of ordered) {
        const start = Date.parse(recording.startedAt);
        if (Number.isNaN(start))
            continue;
        if (instant >= start && instant <= start + recording.durationMs) {
            return { sequence: recording.sequence, seconds: (instant - start) / 1_000 };
        }
    }
    return null;
}
/* -- Retention and the findings audit ---------------------------------------- */
/**
 * Run logs (narrated steps, recording rows, and the step frames and recording
 * objects under `browser-agent/v1/`) are kept for 30 days, except for runs
 * saved as demos. Findings are the audit record and stay permanently.
 */
export const BROWSER_AGENT_LOG_RETENTION_DAYS = 30;
/** Widest range the daily counts and range exports accept. */
export const BROWSER_AGENT_FINDINGS_MAX_RANGE_DAYS = 366;
const dayMs = 86_400_000;
/** The Brasília calendar day (`YYYY-MM-DD`) of an instant (fixed UTC-3). */
export function brasiliaDay(at) {
    const instant = typeof at === "string" ? Date.parse(at) : at.getTime();
    return new Date(instant + BROWSER_AGENT_UTC_OFFSET_MINUTES * 60_000).toISOString().slice(0, 10);
}
/** `YYYY-MM-DD` shifted by whole days. */
export function addDays(day, days) {
    return new Date(Date.parse(`${day}T00:00:00.000Z`) + days * dayMs).toISOString().slice(0, 10);
}
/** Inclusive number of days from `from` to `to` (`YYYY-MM-DD`). */
export function daySpan(from, to) {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / dayMs) + 1;
}
/** `SP-RJ`: origin → destination UF pair used by the findings route filter. */
export const browserAgentRouteFilterSchema = z.string().regex(/^[A-Z]{2}-[A-Z]{2}$/);
export const browserAgentFindingOutcomeCountsSchema = z
    .object({
    ST_FOUND: z.number().int().nonnegative(),
    NO_ST_FOUND: z.number().int().nonnegative(),
    INTERNAL_ST_ONLY: z.number().int().nonnegative(),
    NCM_NOT_FOUND: z.number().int().nonnegative(),
    UNREADABLE: z.number().int().nonnegative(),
    /** Web research findings only; omitted by servers that predate the lane. */
    INCONCLUSIVE: z.number().int().nonnegative().optional(),
})
    .strict();
export const browserAgentFindingDaySchema = z
    .object({
    /** Brasília calendar day. */
    date: z.string().date(),
    total: z.number().int().nonnegative(),
    byOutcome: browserAgentFindingOutcomeCountsSchema,
})
    .strict();
/** Per-day finding counts, every day of the range included (zeros too). */
export const browserAgentFindingDailySchema = z
    .object({
    timezone: z.literal(BROWSER_AGENT_TIMEZONE),
    from: z.string().date(),
    to: z.string().date(),
    days: z.array(browserAgentFindingDaySchema).max(BROWSER_AGENT_FINDINGS_MAX_RANGE_DAYS),
})
    .strict();
//# sourceMappingURL=browser-agent.js.map